import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { TopBar } from "../components/TopBar";
import { ErrorText } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { parseDbDate, visibleEmail } from "../lib/format";

type ClassDetail = {
  class: {
    id: number;
    name: string;
    invite_code: string;
    org_name: string;
  };
  students: Student[];
};

type LevelInfo = { level: number; label: string; xp_in_level: number; xp_to_next: number; progress_pct: number; is_max: boolean };

type Student = {
  id: number;
  name: string;
  email?: string | null;
  login_id?: string | null;
  total_xp: number;
  current_level: number;
  coin_balance: number;
  streak_days: number;
  last_studied_at: string | null;
  unlocked_areas: string[];
  earned_badges: string[];
  attempt_count: number;
  correct_count: number;
  level: LevelInfo;
};

export function ClassDetailPage() {
  const { t } = useTranslation(["common", "admin"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["teacher-class", id],
    queryFn: () => api<ClassDetail>(`/api/teacher/classes/${id}`),
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
  });

  function lastStudiedLabel(iso: string | null): string {
    if (!iso) return t("admin:never");
    const days = Math.floor((Date.now() - parseDbDate(iso).getTime()) / 86_400_000);
    if (days <= 0) return t("admin:today");
    return t("admin:days_ago", { n: days });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar subtitle={data?.class.name} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/" className="text-sm text-muted underline">{t("common:back")}</Link>

        {isLoading && <p className="text-muted mt-4">{t("common:loading")}</p>}
        {error && <div className="mt-4"><ErrorText>{t("admin:class_unavailable")}</ErrorText></div>}

        {data && (
          <>
            <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-muted">{data.class.org_name}</div>
                <h1 className="text-2xl font-bold text-primary">{data.class.name}</h1>
              </div>
              {data.class.invite_code && (
                <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                  <div className="text-xs text-muted">{t("admin:invite_code")}</div>
                  <div className="text-lg font-mono font-bold text-primary">{data.class.invite_code}</div>
                </div>
              )}
            </div>

            <h2 className="text-lg font-bold text-primary mt-8 mb-3">
              {t("admin:students")}（{t("admin:students_count", { count: data.students.length })}）
            </h2>

            <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                  <tr>
                    <th className="text-left px-4 py-3">{t("admin:col_name")}</th>
                    <th className="text-left px-4 py-3">{t("admin:level")}</th>
                    <th className="text-right px-4 py-3">{t("admin:coins")}</th>
                    <th className="text-right px-4 py-3">{t("admin:streak")}</th>
                    <th className="text-right px-4 py-3">{t("admin:accuracy")}</th>
                    <th className="text-right px-4 py-3">{t("admin:last_studied")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s) => {
                    const acc = s.attempt_count === 0 ? null : Math.round((s.correct_count / s.attempt_count) * 100);
                    const sub = s.login_id ?? visibleEmail(s.email);
                    const go = () => navigate(`/students/${s.id}`);
                    return (
                      <tr
                        key={s.id}
                        data-testid="learner-row"
                        role="link"
                        tabIndex={0}
                        onClick={go}
                        onKeyDown={(e) => { if (e.key === "Enter") go(); }}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <span className="text-primary font-bold">{s.name}</span>
                          {sub && <div className="text-xs text-muted">{sub}</div>}
                          {s.earned_badges.length > 0 && (
                            <div className="text-xs text-ops mt-1">{s.earned_badges.map(badgeLabel).join("・")}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 min-w-[160px]">
                          <div className="flex items-baseline gap-2">
                            <span className="font-bold text-primary text-lg">{s.level?.label ?? "入門"}</span>
                            <span className="text-xs text-muted">{s.level?.progress_pct ?? 0}%</span>
                          </div>
                          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden mt-1">
                            <div className="h-full bg-gradient-to-r from-sakura to-primary" style={{ width: `${s.level?.progress_pct ?? 0}%` }} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{s.coin_balance ?? 0}</td>
                        <td className="px-4 py-3 text-right">{s.streak_days}{t("admin:days")}</td>
                        <td className="px-4 py-3 text-right">
                          {acc == null ? <span className="text-muted">—</span> : (
                            <span className={acc >= 80 ? "text-green-600 font-bold" : acc >= 60 ? "text-orange-600" : "text-accent"}>{acc}%</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-muted text-xs">{lastStudiedLabel(s.last_studied_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function badgeLabel(key: string): string {
  const map: Record<string, string> = {
    hiragana_master: "ひらがな",
    katakana_master: "カタカナ",
    streak_7d: "連続7日",
    streak_30d: "連続30日",
  };
  return map[key] ?? key;
}
